import React, { useState } from 'react';
import { signUp, signInWithGoogle } from '../services/authService'; // Assuming correct path
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUser, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Spinner, InputGroup } from 'react-bootstrap'; // Alert is now in AuthFormLayout

import AuthPageLayout from '../components/AuthPageLayout';
import AuthFormLayout from '../components/AuthFormLayout';
// import './AuthPage.css'; // Styles handled by layouts

const RegisterPage = ({ setUser }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '', email: '', password: '', agreeToTerms: false
    });
    // const [passwordStrength, setPasswordStrength] = useState(''); // UI for this is within the form
    const [showPassword, setShowPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState(''); // Main error for registration
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordCriteria, setPasswordCriteria] = useState({ minLength: false, hasLetter: false, hasDigit: false });
    const [showPasswordCriteriaFeedback, setShowPasswordCriteriaFeedback] = useState(false);

    const validatePassword = (password) => {
        const hasMinLength = password.length >= 6;
        const hasLetter = /[a-zA-Z]/.test(password);
        const hasDigit = /\d/.test(password);
        setPasswordCriteria({ minLength: hasMinLength, hasLetter: hasLetter, hasDigit: hasDigit });
        return hasMinLength && hasLetter && hasDigit;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (name === 'password') {
            if (!showPasswordCriteriaFeedback && value.length > 0) setShowPasswordCriteriaFeedback(true);
            validatePassword(value);
            if (value.length === 0) setShowPasswordCriteriaFeedback(false);
        }
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    const handleGoogleSuccess = async (credentialResponse) => {
        setErrorMsg(''); setIsSubmitting(true);
        try {
            const { user } = await signInWithGoogle(credentialResponse);
            if (user) { setUser(user); navigate('/app'); }
            else { setErrorMsg('Could not retrieve user information after Google sign-in.'); }
        } catch (err) { setErrorMsg(err.message || 'Google sign-in failed.'); }
        finally { setIsSubmitting(false); }
    };
    const handleGoogleError = () => { setErrorMsg('Google Sign-In failed. Please try again.'); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        if (!formData.agreeToTerms) { setErrorMsg('You must agree to the terms and conditions to register.'); return; }
        if (!validatePassword(formData.password)) { setErrorMsg('Password does not meet all the required criteria.'); setShowPasswordCriteriaFeedback(true); return; }
        setIsSubmitting(true);
        try {
            const browserLanguage = navigator.language || navigator.userLanguage;
            const result = await signUp({
                email: formData.email, password: formData.password, fullName: formData.fullName, language: browserLanguage
            });
            if (result && result.user) {
                // setErrorMsg(''); // Not needed as we navigate away or show alert
                alert('Registration successful! Please check your email to confirm your account.'); // Or use a more sophisticated notification
                navigate('/login');
            } else {
                setErrorMsg(result.message || 'Registration completed but no user data returned.');
            }
        } catch (err) {
            setErrorMsg(err.message || 'Registration failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isPasswordGloballyValid = passwordCriteria.minLength && passwordCriteria.hasLetter && passwordCriteria.hasDigit;
    const canSubmit = formData.fullName && formData.email && isPasswordGloballyValid && formData.agreeToTerms && !isSubmitting;

    return (
        <AuthPageLayout>
            <AuthFormLayout
                pageTitle="Create Your Account"
                errorMsg={errorMsg}
                setErrorMsg={setErrorMsg}
                // submitStatus and setSubmitStatus are not used directly by RegisterPage for now, but could be if needed
                onGoogleSuccess={handleGoogleSuccess}
                onGoogleError={handleGoogleError}
                toggleLinkPath="/login"
                toggleLinkText="Log In"
                toggleQuestionText="Already have an account?"
            >
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3 floating-label-group">
                        <Form.Control type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder=" " required />
                        <Form.Label htmlFor="fullName"><FontAwesomeIcon icon={faUser} className="me-2" />Full Name</Form.Label>
                    </Form.Group>
                    <Form.Group className="mb-3 floating-label-group">
                        <Form.Control type="email" id="emailRegister" name="email" value={formData.email} onChange={handleInputChange} placeholder=" " required />
                        <Form.Label htmlFor="emailRegister"><FontAwesomeIcon icon={faEnvelope} className="me-2" />Email Address</Form.Label>
                    </Form.Group>
                    <Form.Group className="mb-3 floating-label-group">
                        <InputGroup>
                            <Form.Control type={showPassword ? "text" : "password"} id="passwordRegister" name="password" value={formData.password} onChange={handleInputChange} placeholder=" " required />
                            <Button variant="outline-secondary" onClick={togglePasswordVisibility} aria-label={showPassword ? "Hide password" : "Show password"}>
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </Button>
                        </InputGroup>
                        <Form.Label htmlFor="passwordRegister">Create Password</Form.Label>
                        {showPasswordCriteriaFeedback && formData.password.length > 0 && (
                            <div id="passwordHelpBlock" className="form-text my-2 ps-1 small"> {/* Adjusted margin */}
                                <div className={passwordCriteria.minLength ? 'text-success' : 'text-danger'}>
                                    {passwordCriteria.minLength ? '✓' : '✗'} At least 6 characters long
                                </div>
                                <div className={passwordCriteria.hasLetter ? 'text-success' : 'text-danger'}>
                                    {passwordCriteria.hasLetter ? '✓' : '✗'} Contains at least one letter
                                </div>
                                <div className={passwordCriteria.hasDigit ? 'text-success' : 'text-danger'}>
                                    {passwordCriteria.hasDigit ? '✓' : '✗'} Contains at least one digit
                                </div>
                            </div>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3 form-check">
                        <Form.Check type="checkbox" id="agreeTerms" name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleInputChange} required />
                        <Form.Label htmlFor="agreeTerms" className="form-check-label terms-label">
                            I agree to the <Link to="/terms" target="_blank">Terms</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                        </Form.Label>
                    </Form.Group>
                    <Button type="submit" className="w-100 text-white mt-3 main-action-button" disabled={!canSubmit}>
                        {isSubmitting ? <Spinner animation="border" size="sm" className="me-2" /> : ''}
                        Create Account
                    </Button>
                </Form>
            </AuthFormLayout>
        </AuthPageLayout>
    );
};

export default RegisterPage;